import { useLayoutEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import Button from "features/components/Button";

import Image from "./Image";

interface Props {
  thumbnails: { url: string; ID: string }[];
  selectedId?: string;
  onClick: (id: string) => void;
  onShowAll?: () => void;
}

const BLUR_WIDTH = "3em";
// This number represents how far from the left (or right) border of "thumbnail-list"
// click still triggers scrolling. Number is set using try/fail/succeed
const BLUR_CLICK_OFFSET = 6;

const ThumbnailList = ({
  thumbnails,
  selectedId,
  onClick,
  onShowAll,
}: Props) => {
  const { t } = useTranslation("common");

  // Auto-scroll to the selected thumbnail if not visible (when navigating thumbnails using previous/next buttons)
  useLayoutEffect(() => {
    if (!selectedId) return;

    const container = containerRef.current;
    if (!container) return;

    const idx = thumbnails.findIndex((t) => t.ID === selectedId);
    if (idx === -1) return;

    const thumbnailDivs = container.querySelectorAll(".w-8.h-8");
    const el = thumbnailDivs[idx] as HTMLElement | undefined;
    onShowAll == null && onThumbnailClick(selectedId, el);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, thumbnails]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftBlur, setShowLeftBlur] = useState(false);
  const [showRightBlur, setShowRightBlur] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkBlur = () => {
    const el = containerRef.current;
    if (!el) return;

    setShowLeftBlur(el.scrollLeft > 2);
    setShowRightBlur(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    setIsOverflowing(el.scrollWidth > el.clientWidth + 2);
  };

  useLayoutEffect(() => {
    checkBlur();
    const el = containerRef.current;
    if (!el) return;

    el.addEventListener("scroll", checkBlur);
    window.addEventListener("resize", checkBlur);

    return () => {
      el.removeEventListener("scroll", checkBlur);
      window.removeEventListener("resize", checkBlur);
    };
  }, [thumbnails]);

  const onThumbnailClick = (ID: string, el: HTMLElement | undefined) => {
    const container = containerRef.current;
    if (container && el) {
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const leftBlurEdge =
        containerRect.left +
        BLUR_CLICK_OFFSET *
          parseFloat(getComputedStyle(document.documentElement).fontSize);
      const rightBlurEdge =
        containerRect.right -
        BLUR_CLICK_OFFSET *
          parseFloat(getComputedStyle(document.documentElement).fontSize);
      if (elRect.right < leftBlurEdge || elRect.left > rightBlurEdge) {
        // Scroll so the thumbnail is centered
        const scrollTo =
          el.offsetLeft - container.clientWidth / 2 + el.clientWidth / 2;

        setTimeout(() => {
          container.scrollTo({ left: scrollTo, behavior: "smooth" });
        }, 100);
      }
    }

    onClick(ID);
  };

  return (
    <div className="flex flex-row gap-4 items-center">
      <div style={{ position: "relative" }} className="flex-1 min-w-0">
        <div
          ref={containerRef}
          id="thumbnail-list"
          className={classNames(
            "flex gap-4 flex-row flex-nowrap overflow-x-auto items-center hide-scrollbar",
            isOverflowing ? "justify-start" : "justify-center",
            onShowAll ? "p-2" : "p-4"
          )}
          style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}
        >
          {thumbnails.map(({ ID, url }) => (
            <div
              className="w-8 h-8 rounded-[2px] cursor-pointer flex-shrink-0 bg-[#E2E8F1]"
              key={ID}
            >
              <Image
                photoSrc={url}
                photoId={ID}
                className={classNames("!rounded-[2px]", {
                  "ring-2 ring-gray-600 ring-offset-2": selectedId == ID,
                })}
                onClick={(_, event: any) => {
                  const element = event.currentTarget.parentElement;
                  onThumbnailClick(ID, element);
                }}
                isThumbnail
              />
            </div>
          ))}
        </div>
        {/* Left Blur Overlay */}
        {showLeftBlur && (
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: BLUR_WIDTH,
              height: "100%",
              pointerEvents: "none",
              zIndex: 1,
              background:
                "linear-gradient(to right, rgba(255,255,255,0.8) 60%, rgba(255,255,255,0))",
              filter: "blur(0.5px)",
            }}
          />
        )}
        {/* Right Blur Overlay */}
        {showRightBlur && (
          <div
            style={{
              position: "absolute",
              right: 0,
              top: 0,
              width: BLUR_WIDTH,
              height: "100%",
              pointerEvents: "none",
              zIndex: 1,
              background:
                "linear-gradient(to left, rgba(255,255,255,0.8) 60%, rgba(255,255,255,0))",
              filter: "blur(0.5px)",
            }}
          />
        )}
      </div>
      {onShowAll != null && (
        <div className="pr-2">
          <Button
            color="secondary"
            variant="contained"
            size="small"
            onClick={onShowAll}
          >
            {t("images.view", { count: thumbnails.length })}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ThumbnailList;
